'use client'

import { useId, useState } from 'react'
import { ActionButton } from '@/components/parts/action-button'
import { Chip } from '@/components/parts/chip'

export type FilterGroup = {
  /** 状態を引くためのキー */
  key: string
  /** 行の見出し（業種 / 導入部署 …） */
  label: string
  items: string[]
}

type FilterPanelProps = {
  groups: FilterGroup[]
  /** グループキー → 選択中の項目名 */
  selected: Record<string, string[]>
  onToggle: (groupKey: string, item: string) => void
  keyword: string
  onKeywordChange: (value: string) => void
  onSubmit: () => void
  /** 見出しの文言。既定は「条件で絞り込む」 */
  heading?: string
  keywordLabel?: string
  keywordPlaceholder?: string
  submitLabel?: string
  /** 初期状態で条件を開くか。既定は閉じた状態 */
  defaultOpen?: boolean
}

/**
 * 導入事例一覧などの「条件で絞り込む」。
 * State=open は条件を開いた状態、close は見出しの行だけ。
 */
export function FilterPanel({
  groups,
  selected,
  onToggle,
  keyword,
  onKeywordChange,
  onSubmit,
  heading = '条件で絞り込む',
  keywordLabel = 'キーワード',
  keywordPlaceholder = 'テキストを入力してください',
  submitLabel = '選択した条件で絞り込む',
  defaultOpen = false
}: FilterPanelProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const bodyId = useId()
  const keywordId = useId()

  return (
    <section className="flex w-full flex-col items-center gap-8 rounded-xl bg-surface-subtle p-6 lg:p-8">
      {/* 見出しの行全体（パネルの余白を含む）を開閉ボタンにする。負のマージンで余白まで広げ、同じ量の padding で中身の位置を保つ */}
      <h2 className="-m-6 self-stretch lg:-m-8">
        <button
          type="button"
          className="flex w-full cursor-pointer items-center gap-5 rounded-xl p-6 text-left text-ink focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-line-inverse lg:p-8"
          aria-expanded={isOpen}
          aria-controls={bodyId}
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <span className="flex min-w-0 flex-1 items-center gap-3">
            <span
              aria-hidden="true"
              className="icon icon-filter size-8 shrink-0 lg:size-10"
            />
            <span className="fontfeaturesettings text-xl font-bold leading-compact tracking-heading lg:text-2xl">
              {heading}
            </span>
          </span>
          <span
            aria-hidden="true"
            className={`icon icon-chevron-up size-8 shrink-0 transition-transform duration-200 lg:size-10 ${
              isOpen ? '' : 'rotate-180'
            }`}
          />
        </button>
      </h2>

      <div id={bodyId} hidden={!isOpen} className="w-full">
        <div className="flex w-full flex-col gap-8">
          <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-center lg:gap-8">
            <label
              htmlFor={keywordId}
              className="w-25 shrink-0 text-base font-bold leading-compact text-ink lg:text-lg"
            >
              {keywordLabel}
            </label>
            <div className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-line bg-surface px-4 py-3">
              <input
                id={keywordId}
                type="search"
                value={keyword}
                placeholder={keywordPlaceholder}
                onChange={(event) => onKeywordChange(event.target.value)}
                className="min-w-0 flex-1 bg-transparent text-base leading-body text-ink outline-none placeholder:text-ink-subtle"
              />
              <img
                src="/images/icons/icon-search.svg"
                alt=""
                width={20}
                height={20}
                className="size-5 shrink-0 text-ink"
              />
            </div>
          </div>

          <div className="flex w-full flex-col gap-5">
            {groups.map((group, index) => (
              <div key={group.key} className="flex w-full flex-col gap-5">
                {index > 0 ? (
                  <hr className="w-full border-t border-line-subtle" />
                ) : null}
                <div className="flex w-full flex-col gap-3 lg:flex-row lg:items-start lg:gap-8">
                  <p className="w-25 shrink-0 text-base font-bold leading-compact text-ink lg:text-lg">
                    {group.label}
                  </p>
                  <ul className="flex min-w-0 flex-1 flex-wrap items-start gap-3">
                    {group.items.map((item) => (
                      <li key={item}>
                        <Chip
                          label={item}
                          selected={(selected[group.key] ?? []).includes(item)}
                          onClick={() => onToggle(group.key, item)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          <div className="flex w-full justify-center">
            <ActionButton label={submitLabel} onClick={onSubmit} />
          </div>
        </div>
      </div>
    </section>
  )
}
